/**
 * Platform Console → Active Users. Shows who has entered the platform (across ALL
 * tenants) in a chosen window (today / 7d / 30d / custom), with a headline count
 * and a per-user list. Clicking a user opens their journey. Platform-admin only
 * (the analytics endpoints require admin).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Typography, CircularProgress, Alert, Button, ToggleButton, ToggleButtonGroup,
  FormControlLabel, Switch, TextField, Chip, Avatar,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getAnalyticsUsers } from "./api/tenantApi";
import JourneyDrawer from "./analytics/JourneyDrawer";

const INK = "#0B2A4A", BLUE = "#1E6FE8", MUTED = "#64748b", LINE = "#e3ebf3";

const fmt = (n) => (n == null ? "0" : Number(n).toLocaleString());
const shortId = (id) => (id ? String(id).slice(0, 8) : "—");
const fmtRelative = (ms) => {
  if (!ms) return "—";
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const RANGES = [{ label: "Today", days: 1 }, { label: "7 days", days: 7 }, { label: "30 days", days: 30 }, { label: "Custom", days: "custom" }];
const FILTER_DEBOUNCE_MS = 300;

export default function PlatformActiveUsers() {
  const [days, setDays] = useState(7);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [journeyUser, setJourneyUser] = useState(null);
  const loadRequest = useRef(0);

  const params = useMemo(() => {
    if (days === "custom") {
      const f = from ? Date.parse(from) : NaN;
      const t = to ? Date.parse(to) + 86400000 - 1 : Date.now(); // include the whole 'to' day
      if (!Number.isFinite(f)) return null; // wait for a valid from date
      return { from: f, to: t, includeAdmins };
    }
    return { days, includeAdmins };
  }, [days, from, to, includeAdmins]);

  const load = useCallback(async () => {
    const requestId = ++loadRequest.current;
    if (!params) { setUsers([]); setLoading(false); return; }
    setLoading(true); setError("");
    try {
      const data = await getAnalyticsUsers(params);
      if (requestId !== loadRequest.current) return;
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      if (requestId !== loadRequest.current) return;
      setError(e?.response?.data?.message || e?.message || "Failed to load active users");
      setUsers([]);
    } finally {
      if (requestId === loadRequest.current) setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    const timer = setTimeout(load, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [load]);

  const rangeLabel = days === "custom" ? "in the selected range" : days === 1 ? "today" : `in the last ${days} days`;

  return (
    <Box>
      {/* controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v != null && setDays(v)}
          sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.5, py: 0.4, color: MUTED, borderColor: LINE },
                "& .Mui-selected": { color: `${INK} !important`, background: "#eaf1fe !important" } }}>
          {RANGES.map((r) => <ToggleButton key={String(r.days)} value={r.days}>{r.label}</ToggleButton>)}
        </ToggleButtonGroup>

        {days === "custom" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
            <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
          </Box>
        )}

        <FormControlLabel control={<Switch size="small" checked={includeAdmins} onChange={(e) => setIncludeAdmins(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 12.5, color: MUTED }}>Include internal</Typography>} />
        <Button onClick={load} startIcon={<RefreshIcon />} size="small" sx={{ textTransform: "none", color: BLUE, fontWeight: 700, ml: "auto" }}>Refresh</Button>
      </Box>

      {/* headline stat */}
      <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "14px", background: "#fff", p: 2.25, mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ width: 42, height: 42, borderRadius: "12px", background: `${BLUE}14`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <GroupIcon sx={{ color: BLUE }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 800, color: INK, lineHeight: 1 }}>{loading ? "…" : fmt(users.length)}</Typography>
          <Typography sx={{ fontSize: 13, color: MUTED }}>users entered {rangeLabel} · across all tenants</Typography>
        </Box>
      </Box>

      {error && <Alert severity="info" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}
      {days === "custom" && !params && <Alert severity="info" sx={{ mb: 2, borderRadius: "12px" }}>Pick a "From" date to see users.</Alert>}

      {/* user list */}
      <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "14px", background: "#fff", p: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={26} /></Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse",
              "& th": { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: MUTED, fontWeight: 700, py: 0.75, borderBottom: `1px solid ${LINE}` },
              "& td": { fontSize: 13, color: INK, py: 1, borderBottom: `1px solid ${LINE}` } }}>
              <thead>
                <tr><th>User</th><th>Tenant</th><th>Sessions</th><th>Events</th><th>Last seen</th></tr>
              </thead>
              <tbody>
                {users.length ? users.slice(0, 300).map((u) => (
                  <Box component="tr" key={u.userId} onClick={() => setJourneyUser(u)}
                    sx={{ cursor: "pointer", "&:hover td": { background: "#f4f8ff" } }}>
                    <td>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 26, height: 26, fontSize: 12, fontWeight: 800, bgcolor: "#eaf1fe", color: BLUE }}>
                          {(u.email || u.userId || "?").slice(0, 1).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK }}>{u.email || shortId(u.userId)}</Typography>
                          {u.isAdmin && <Chip label="internal" size="small" sx={{ height: 16, fontSize: 10, background: "#fff4e5", color: "#B76E00" }} />}
                        </Box>
                      </Box>
                    </td>
                    <td><Typography sx={{ fontSize: 12.5, color: MUTED, fontFamily: "monospace" }}>{shortId(u.tenantId)}</Typography></td>
                    <td>{fmt(u.sessions)}</td>
                    <td>{fmt(u.events)}</td>
                    <td><Typography sx={{ fontSize: 12.5, color: MUTED }}>{fmtRelative(u.lastSeen)}</Typography></td>
                  </Box>
                )) : <tr><td colSpan={5}><Typography sx={{ fontSize: 13, color: MUTED, py: 3, textAlign: "center" }}>No users entered {rangeLabel}.</Typography></td></tr>}
              </tbody>
            </Box>
          </Box>
        )}
      </Box>

      <JourneyDrawer open={!!journeyUser} user={journeyUser} onClose={() => setJourneyUser(null)} />
    </Box>
  );
}
